import { useCallback, useEffect, useRef, useState } from "react";

import { saveResponse, type Disposition, type Journey } from "../api";
import {
  questions as questionCopy,
  steps as stepCopy,
  ui,
} from "../content/en";
import { interpolate } from "./format";

type SaveState = "idle" | "saving" | "saved" | "failed";

interface Props {
  readonly journey: Journey;
  readonly credential: string;
  readonly vars: Readonly<Record<string, string>>;
  readonly onSaved: (journey: Journey) => void;
}

const AUTOSAVE_DELAY_MS = 900;

function QuestionCard({
  credential,
  questionKey,
  vars,
  initialText,
  initialDisposition,
  readOnly,
  onSaved,
}: {
  readonly credential: string;
  readonly questionKey: string;
  readonly vars: Readonly<Record<string, string>>;
  readonly initialText: string;
  readonly initialDisposition: Disposition | null;
  readonly readOnly: boolean;
  readonly onSaved: (journey: Journey) => void;
}): React.JSX.Element {
  const [text, setText] = useState(initialText);
  const [disposition, setDisposition] = useState<Disposition | null>(
    initialDisposition,
  );
  const [state, setState] = useState<SaveState>("idle");
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const latest = useRef(initialText);

  const persist = useCallback(
    async (next: Disposition, body: string | null): Promise<void> => {
      setState("saving");
      try {
        const updated = await saveResponse(credential, questionKey, next, body);
        onSaved(updated);
        if (latest.current.trim() === (body ?? "")) {
          setState("saved");
        }
      } catch {
        setState("failed");
      }
    },
    [credential, questionKey, onSaved],
  );

  useEffect(
    () => () => {
      if (timer.current !== undefined) clearTimeout(timer.current);
    },
    [],
  );

  function onChange(value: string): void {
    setText(value);
    latest.current = value;
    setDisposition("ANSWERED");
    setState("saving");
    if (timer.current !== undefined) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const bounded = latest.current.trim();
      void persist(
        bounded.length === 0 ? "SKIPPED" : "ANSWERED",
        bounded.length === 0 ? null : bounded,
      );
    }, AUTOSAVE_DELAY_MS);
  }

  function defer(): void {
    if (timer.current !== undefined) clearTimeout(timer.current);
    setDisposition("DEFERRED_TO_INTERVIEW");
    void persist("DEFERRED_TO_INTERVIEW", null);
  }

  const deferred = disposition === "DEFERRED_TO_INTERVIEW";
  const stateLabel =
    state === "saving"
      ? ui.saving
      : state === "saved"
        ? deferred
          ? ui.discussSelected
          : ui.saved
        : state === "failed"
          ? ui.saveFailed
          : "";

  return (
    <li className="question">
      <label htmlFor={`q-${questionKey}`}>
        {interpolate(questionCopy[questionKey] ?? questionKey, vars)}
      </label>
      <textarea
        id={`q-${questionKey}`}
        value={text}
        maxLength={4000}
        disabled={readOnly}
        placeholder={ui.answerPlaceholder}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      <div className="question-actions">
        <button
          type="button"
          className={`btn small${deferred ? " toggled" : ""}`}
          disabled={readOnly}
          aria-pressed={deferred}
          onClick={defer}
        >
          {ui.discussInstead}
        </button>
        <p
          className="save-state"
          data-state={state}
          role="status"
          aria-live="polite"
        >
          {stateLabel}
        </p>
      </div>
    </li>
  );
}

function calendarFile(
  scheduledAt: string,
  location: string | null,
  role: string,
): string {
  const start = new Date(scheduledAt);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  const stamp = (value: Date): string =>
    `${value.toISOString().replace(/[-:]/g, "").split(".")[0] ?? ""}Z`;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Zhatura//Ambassador Journey//EN",
    "BEGIN:VEVENT",
    `DTSTAMP:${stamp(new Date())}`,
    `DTSTART:${stamp(start)}`,
    `DTEND:${stamp(end)}`,
    `SUMMARY:Zhatura conversation - ${role}`,
    location === null ? "" : `LOCATION:${location}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter((line) => line.length > 0);
  return lines.join("\r\n");
}

export function Perspective({
  journey,
  credential,
  vars,
  onSaved,
}: Props): React.JSX.Element {
  const copy = stepCopy["your-perspective"];
  const step = journey.program.steps.find(
    (candidate) => candidate.key === "your-perspective",
  );
  const completed = journey.status === "COMPLETED";
  const saved = new Map(
    journey.responses.map((response) => [response.question_key, response]),
  );

  const meeting = journey.meeting;
  const calendarHref =
    meeting === null
      ? null
      : `data:text/calendar;charset=utf-8,${encodeURIComponent(
          calendarFile(meeting.scheduled_at, meeting.location, vars.Role ?? ""),
        )}`;

  return (
    <>
      <p className="eyebrow">{copy?.eyebrow ?? ""}</p>
      <h1>{interpolate(copy?.title ?? "", vars)}</h1>
      {copy?.body?.map((paragraph) => (
        <p className="lede" key={paragraph}>
          {interpolate(paragraph, vars)}
        </p>
      ))}
      <ul className="cards" style={{ gridTemplateColumns: "1fr", gap: "1rem" }}>
        {(step?.questions ?? []).map((question) => {
          const existing = saved.get(question.key);
          return (
            <QuestionCard
              key={question.key}
              credential={credential}
              questionKey={question.key}
              vars={vars}
              initialText={existing?.answer_text ?? ""}
              initialDisposition={existing?.disposition ?? null}
              readOnly={completed}
              onSaved={onSaved}
            />
          );
        })}
      </ul>
      {completed && (
        <section className="completion" aria-labelledby="completion-title">
          <h2 id="completion-title">{interpolate(ui.completionTitle, vars)}</h2>
          <p>{ui.completionLede}</p>
          {meeting !== null && calendarHref !== null ? (
            <>
              <p>
                <strong>{ui.meetingTitle}:</strong>{" "}
                {new Date(meeting.scheduled_at).toLocaleString()}
                {meeting.location === null ? "" : ` · ${meeting.location}`}
              </p>
              <a
                className="btn"
                href={calendarHref}
                download="zhatura-conversation.ics"
              >
                {ui.addToCalendar}
              </a>
            </>
          ) : (
            <p>{ui.meetingPending}</p>
          )}
        </section>
      )}
    </>
  );
}
